import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { padding, size, color, space } from 'styled-system';
import { yellow400, pink300, green500, purple500, arc99999, shade000, shade700, fontXSmall, fontSmall, fontLarge, font2XLarge, shade400 } from '@pleo-io/telescope-tokens';
import { Inline } from '@pleo-io/telescope-layout';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var colors = [yellow400, pink300, green500, purple500, pink300, yellow400];

function stringToInteger(string) {
  var total = 0;

  for (var i = 0; i !== string.length; i++) {
    if (total >= Number.MAX_SAFE_INTEGER) {
      break;
    }

    total += string.charCodeAt(i);
  }

  return total;
}

var randomColor = function randomColor(seed) {
  var index = stringToInteger(seed) % colors.length;
  return colors[index];
};

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n    position: absolute;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    border-radius: ", ";\n    transform: translate(25%, 25%);\n    right: 0;\n    bottom: 0;\n    box-sizing: border-box;\n    background-color: ", ";\n    ", "\n    ", "\n    ", "\n\n    [data-telescope-icon] {\n        width: 100%;\n        height: 100%;\n    }\n"]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n    width: 100%;\n    height: 100%;\n    object-fit: cover;\n    border-radius: ", ";\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    position: relative;\n    display: inline-flex;\n    flex-shrink: 0;\n    align-items: center;\n    justify-content: center;\n    width: ", "px;\n    height: ", "px;\n    font-size: ", ";\n    color: ", ";\n    border-radius: ", ";\n    border: ", ";\n    background-color: ", ";\n    ", "\n    ", "\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

var fontSizeMapping = {
  24: fontXSmall,
  40: fontSmall,
  48: fontLarge,
  72: font2XLarge
};
var iconSizeMapping = {
  24: 12,
  40: 18,
  48: 24,
  72: 32
};
var AvatarCircle = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject(), function (_ref) {
  var $size = _ref.$size;
  return $size != null ? $size : 48;
}, function (_ref2) {
  var $size = _ref2.$size;
  return $size != null ? $size : 48;
}, function (_ref3) {
  var _ref3$$size = _ref3.$size,
      $size = _ref3$$size === void 0 ? 48 : _ref3$$size;
  return fontSizeMapping[$size];
}, shade700, arc99999, function (_ref4) {
  var $showBorder = _ref4.$showBorder;
  return $showBorder ? "3px solid " + shade000 : "none";
}, function (_ref5) {
  var _ref5$$name = _ref5.$name,
      $name = _ref5$$name === void 0 ? '' : _ref5$$name;
  return randomColor($name);
}, color, space);
var AvatarImage = /*#__PURE__*/styled.img.attrs(function (_ref6) {
  var $name = _ref6.$name;
  return {
    alt: $name
  };
})( /*#__PURE__*/_templateObject2(), arc99999);
var AvatarBadge = /*#__PURE__*/styled.div.attrs(function (props) {
  var _props$padding, _props$size;

  return {
    padding: (_props$padding = props.padding) != null ? _props$padding : 4,
    size: (_props$size = props.size) != null ? _props$size : 24
  };
})( /*#__PURE__*/_templateObject3(), arc99999, shade000, padding, size, color);
/**
 * React hook that loads an image in the browser,
 * and let's us know the `status` so we can show image
 * fallback if it is still `pending`
 *
 * @returns the status of the image loading progress
 *
 * @example
 *
 * ```jsx
 * function App(){
 *   const status = useImage({ src: "image.png" })
 *   return status === "loaded" ? <img src="image.png" /> : <Placeholder />
 * }
 * ```
 */

function useImage(props) {
  var src = props.src,
      onLoad = props.onLoad,
      onError = props.onError;

  var _useState = useState('pending'),
      status = _useState[0],
      setStatus = _useState[1];

  useEffect(function () {
    setStatus(src ? 'loading' : 'pending');
  }, [src]);
  var imageRef = useRef();
  var load = useCallback(function () {
    if (!src) {
      return;
    }

    clearRef();
    var img = new Image();
    img.src = src;

    img.onload = function (event) {
      clearRef();
      setStatus('loaded');
      onLoad == null ? void 0 : onLoad(event);
    };

    img.onerror = function (error) {
      clearRef();
      setStatus('failed');
      onError == null ? void 0 : onError(error);
    };

    imageRef.current = img;
  }, [src, onLoad, onError]);

  var clearRef = function clearRef() {
    if (imageRef.current) {
      imageRef.current.onload = null;
      imageRef.current.onerror = null;
      imageRef.current = null;
    }
  };

  useLayoutEffect(function () {
    if (status === 'loading') {
      load();
    }

    return function () {
      clearRef();
    };
  }, [status, load]);
  return status;
}

var AVATAR_DEFAULT_SIZE = 48;

var Avatar = function Avatar(_ref) {
  var name = _ref.name,
      src = _ref.src,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? AVATAR_DEFAULT_SIZE : _ref$size,
      icon = _ref.icon,
      _ref$getInitials = _ref.getInitials,
      getInitials = _ref$getInitials === void 0 ? initials : _ref$getInitials,
      showBorder = _ref.showBorder,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["name", "src", "size", "icon", "getInitials", "showBorder", "children"]);

  return React.createElement(AvatarCircle, Object.assign({
    "$name": name,
    "$size": size,
    "$showBorder": showBorder
  }, props), React.createElement(AvatarImage$1, {
    name: name,
    getInitials: getInitials,
    src: src,
    icon: icon,
    size: size
  }), children);
};

function initials(name) {
  var _name$toUpperCase$spl = name.toUpperCase().split(' '),
      firstName = _name$toUpperCase$spl[0],
      lastName = _name$toUpperCase$spl[1];

  return firstName && lastName ? "" + firstName.charAt(0) + lastName.charAt(0) : firstName.charAt(0);
}

var AvatarName = function AvatarName(_ref2) {
  var name = _ref2.name,
      getInitials = _ref2.getInitials;
  return React.createElement("div", {
    "aria-label": name
  }, name ? getInitials == null ? void 0 : getInitials(name) : null);
};

var AvatarImage$1 = function AvatarImage$1(_ref3) {
  var icon = _ref3.icon,
      name = _ref3.name,
      getInitials = _ref3.getInitials,
      src = _ref3.src,
      _ref3$size = _ref3.size,
      size = _ref3$size === void 0 ? AVATAR_DEFAULT_SIZE : _ref3$size;
  var status = useImage({
    src: src
  });
  var hasLoaded = status === 'loaded';
  /**
   * Fallback avatar applies under 2 conditions:
   * - If `src` was passed and the image has not loaded or failed to load
   * - If `src` wasn't passed
   *
   * In this case, we'll show either the initials avatar
   */

  var showFallback = !src || !hasLoaded;

  if (icon) {
    return React.cloneElement(icon, {
      role: 'img',
      size: iconSizeMapping[size]
    });
  }

  if (showFallback) {
    return React.createElement(AvatarName, {
      name: name,
      getInitials: getInitials
    });
  }

  return React.createElement(AvatarImage, {
    "$name": name,
    src: src
  });
};

function getValidChildren(children) {
  return React.Children.toArray(children).filter(function (child) {
    return React.isValidElement(child);
  });
}

var AvatarGroup = function AvatarGroup(_ref) {
  var max = _ref.max,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? AVATAR_DEFAULT_SIZE : _ref$size,
      space = _ref.space,
      children = _ref.children;
  var validChildren = getValidChildren(children);
  var childrenWithinMax = max ? validChildren.slice(0, max) : validChildren;
  var excess = max !== undefined && validChildren.length - max;
  var childrenExtended = childrenWithinMax.map(function (child) {
    return React.cloneElement(child, {
      size: size
    });
  });
  return React.createElement(Inline, {
    space: space
  }, childrenExtended, excess > 0 && React.createElement(Avatar, {
    bg: shade400,
    size: size,
    "data-avatar-excess": true
  }, "+", excess));
};

export { AVATAR_DEFAULT_SIZE, Avatar, AvatarBadge, AvatarGroup, randomColor };